// src/components/SCMConfig.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem,
    FormControl,
    InputLabel,
    Select,
    Chip,
    List,
    ListItem,
    ListItemText
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';


import { database } from "../firebase";
import { ref, push, set, onValue, update, remove, query, orderByChild, equalTo, off } from "firebase/database";

// Function to generate a short unique ID (if needed)
const generateShortId = () => {
    return Math.random().toString(36).substring(2, 9); // Example 7-character ID
};

// Define fixed step name options and their default order
const stepNameOptions = [
    //{ label: "Persetujuan Section Head", value: "Persetujuan Section Head", defaultOrder: 1 },
    //{ label: "Persetujuan SubDep Head", value: "Persetujuan SubDep Head", defaultOrder: 2 },
    //{ label: "Persetujuan Dep Head", value: "Persetujuan Dep Head", defaultOrder: 3 },
    //{ label: "Verifikasi Akhir", value: "Verifikasi Akhir", defaultOrder: 4 },
    // Add more fixed steps here if needed
    { label: "Inventory Verification", value: "Inventory Verification", defaultOrder: 1 },
    { label: "SubDep Head Verification", value: "SubDep Head Verification", defaultOrder: 2 },
    { label: "Dep Head Verification", value: "Dep Head Verification", defaultOrder: 3 },
    { label: "Cost Control Verification", value: "Cost Control Verification", defaultOrder: 4 },
];

function SCMConfig({ title, dbPath, fieldName, primaryKeyField, formFields, tableHeaders }) {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [anchorEl, setAnchorEl] = useState(null);
    const fileInputRef = useRef(null);

    // New state for Approval Steps Dialog
    const [openApprovalStepsDialog, setOpenApprovalStepsDialog] = useState(false);
    const [currentApprovalSteps, setCurrentApprovalSteps] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(null); // For editing approval step
    // Modified stepFormValues to include both target_jabatan_ids and target_role_ids
    const [stepFormValues, setStepFormValues] = useState({ step_name: '', order: '', target_jabatan_ids: [], target_role_ids: [] });

    // State for list of jabatan (positions) (needed for target_jabatan_ids dropdown)
    const [allJabatan, setAllJabatan] = useState([]);
    // State for list of roles (needed for target_role_ids dropdown)
    const [allRoles, setAllRoles] = useState([]);

    // Fetch all jabatan from Firebase
    useEffect(() => {
        const jabatanRef = ref(database, 'db_config/db_wfms/tb_jabatan');
        const unsubscribeJabatan = onValue(jabatanRef, (snapshot) => {
            const jabatanData = [];
            snapshot.forEach((childSnapshot) => {
                const jab = childSnapshot.val();
                if (jab.id_jabatan && jab.nama_jabatan) {
                    jabatanData.push({ label: jab.nama_jabatan, value: jab.id_jabatan });
                }
            });
            setAllJabatan(jabatanData);
        }, (error) => {
            console.error("Error fetching jabatan for SCMConfig:", error);
        });

        // Fetch all roles from Firebase
        const rolesRef = ref(database, 'db_config/db_wfms/tb_role');
        const unsubscribeRoles = onValue(rolesRef, (snapshot) => {
            const rolesData = [];
            snapshot.forEach((childSnapshot) => {
                const role = childSnapshot.val();
                if (role.id_role && role.nama_role) {
                    rolesData.push({ label: role.nama_role, value: role.id_role });
                }
            });
            setAllRoles(rolesData);
        }, (error) => {
            console.error("Error fetching roles for SCMConfig:", error);
        });

        return () => {
            off(jabatanRef, 'value', unsubscribeJabatan);
            off(rolesRef, 'value', unsubscribeRoles);
        };
    }, []);


    // Fetch data from Firebase
    useEffect(() => {
        const dbRef = ref(database, dbPath);
        const unsubscribe = onValue(dbRef, (snapshot) => {
            const dataArr = [];
            snapshot.forEach((childSnapshot) => {
                dataArr.push({ id: childSnapshot.key, ...childSnapshot.val() });
            });
            setData(dataArr);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching data:", error);
            setLoading(false);
            setSnackbar({ open: true, message: "Gagal memuat data: " + error.message, severity: "error" });
        });

        return () => {
            off(dbRef, 'value', unsubscribe);
        };
    }, [dbPath]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenFormDialog = (row = null) => {
        setSelectedRow(row);
        let initialFormValues = {};
        if (row) {
            initialFormValues = { ...row };
            // Convert approval_steps object to array for editing
            if (row.approval_steps && typeof row.approval_steps === 'object') {
                const stepsArray = Object.entries(row.approval_steps).map(([key, value]) => ({
                    id: key, // Use key as a temporary ID for editing
                    ...value,
                    // Ensure order is a number
                    order: typeof value.order === 'number' ? value.order : parseInt(value.order, 10) || 0,
                    // Ensure both target_jabatan_ids and target_role_ids exist
                    target_jabatan_ids: value.target_jabatan_ids || [],
                    target_role_ids: value.target_role_ids || []
                }));
                setCurrentApprovalSteps(stepsArray.sort((a,b) => a.order - b.order));
            } else {
                setCurrentApprovalSteps([]);
            }
        } else {
            initialFormValues = formFields.reduce((acc, field) => ({ ...acc, [field.id]: field.defaultValue || "" }), {});
            setCurrentApprovalSteps([]);
        }
        setFormValues(initialFormValues);
        setOpenFormDialog(true);
    };

    const handleCloseFormDialog = () => {
        setOpenFormDialog(false);
        setSelectedRow(null);
        setFormValues({});
        setCurrentApprovalSteps([]); // Reset steps
    };

    const handleFormChange = (e) => {
        const { id, value } = e.target;
        setFormValues((prev) => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (fieldId) => (event) => {
        setFormValues((prev) => ({ ...prev, [fieldId]: event.target.value }));
    };

    const handleChipDelete = (fieldId, chipToDelete) => () => {
        setFormValues((prev) => ({
            ...prev,
            [fieldId]: (prev[fieldId] || []).filter((chip) => chip !== chipToDelete),
        }));
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSubmit = async () => {
        try {
            const dataToSave = { ...formValues };
            let successMessage = "";

            // Convert currentApprovalSteps (array) to JSON object to be stored in Firebase
            const approvalStepsObject = {};
            currentApprovalSteps.forEach(step => {
                const stepKey = step.id || generateShortId(); // Use existing ID or generate new
                approvalStepsObject[stepKey] = {
                    step_name: step.step_name,
                    order: step.order,
                    target_jabatan_ids: step.target_jabatan_ids || [], // Ensure this is saved
                    target_role_ids: step.target_role_ids || [] // Ensure this is saved
                };
            });
            dataToSave.approval_steps = approvalStepsObject;

            if (selectedRow) {
                const itemRef = ref(database, `${dbPath}/${selectedRow.id}`);
                await update(itemRef, dataToSave);
                successMessage = "Data berhasil diperbarui!";
            } else {
                const newItemRef = push(ref(database, dbPath));
                await set(newItemRef, dataToSave);
                successMessage = "Data berhasil ditambahkan!";
            }
            setSnackbar({ open: true, message: successMessage, severity: "success" });
            handleCloseFormDialog();
        } catch (error) {
            console.error("Error saving data:", error);
            setSnackbar({ open: true, message: "Gagal menyimpan data: " + error.message, severity: "error" });
        }
    };

    const handleOpenDeleteDialog = (row) => {
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    const confirmDelete = async () => {
        try {
            if (selectedRow) {
                const itemRef = ref(database, `${dbPath}/${selectedRow.id}`);
                await remove(itemRef);
                setSnackbar({ open: true, message: "Data berhasil dihapus!", severity: "success" });
            }
            handleCloseDeleteDialog();
        } catch (error) {
            console.error("Error deleting data:", error);
            setSnackbar({ open: true, message: "Gagal menghapus data: " + error.message, severity: "error" });
        }
    };

    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleExport = () => {
        const dataToExport = data.map(row => {
            const newRow = { ...row };
            // Convert approval_steps back to JSON string for export
            if (newRow.approval_steps && typeof newRow.approval_steps === 'object') {
                newRow.approval_steps = JSON.stringify(newRow.approval_steps, null, 2);
            }
            return newRow;
        });
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(dataBlob, `${title.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleImport = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const jsonData = XLSX.utils.sheet_to_json(ws);

                const dbRef = ref(database, dbPath);
                const updates = {};
                jsonData.forEach(row => {
                    const id = row[primaryKeyField] || push(dbRef).key;

                    if (row.approval_steps && typeof row.approval_steps === 'string') {
                        try {
                            row.approval_steps = JSON.parse(row.approval_steps);
                        } catch (e) {
                            console.error("Error parsing JSON in imported data for approval_steps:", e);
                            // Continue but warn or skip this row
                        }
                    }
                    updates[id] = row;
                });

                update(dbRef, updates)
                    .then(() => {
                        setSnackbar({ open: true, message: "Data berhasil diimpor!", severity: "success" });
                    })
                    .catch((error) => {
                        console.error("Error importing data:", error);
                        setSnackbar({ open: true, message: "Gagal mengimpor data: " + error.message, severity: "error" });
                    });
            };
            reader.readAsBinaryString(file);
        }
    };

    // --- LOGIC FOR APPROVAL STEPS DIALOG ---
    const handleOpenApprovalStepsDialog = () => {
        setOpenApprovalStepsDialog(true);
    };

    const handleCloseApprovalStepsDialog = () => {
        setOpenApprovalStepsDialog(false);
        setCurrentStepIndex(null);
        // Reset stepFormValues to include both target_jabatan_ids and target_role_ids
        setStepFormValues({ step_name: '', order: '', target_jabatan_ids: [], target_role_ids: [] });
    };

    const handleAddStep = () => {
        // Initialize with default order and empty arrays for both
        setStepFormValues({ step_name: '', order: currentApprovalSteps.length + 1, target_jabatan_ids: [], target_role_ids: [] });
        setCurrentStepIndex(null); // Add mode
        setOpenApprovalStepsDialog(true);
    };

    const handleEditStep = (index) => {
        const stepToEdit = currentApprovalSteps[index];
        setCurrentStepIndex(index);
        setStepFormValues({
            step_name: stepToEdit.step_name,
            order: stepToEdit.order,
            target_jabatan_ids: stepToEdit.target_jabatan_ids || [],
            target_role_ids: stepToEdit.target_role_ids || []
        });
        setOpenApprovalStepsDialog(true);
    };

    const handleDeleteStep = (indexToDelete) => {
        setCurrentApprovalSteps(prevSteps => prevSteps.filter((_, index) => index !== indexToDelete));
    };

    const handleSaveStep = () => {
        const newStep = {
            id: stepFormValues.id || generateShortId(), // Retain existing ID or generate new
            step_name: stepFormValues.step_name,
            order: parseInt(stepFormValues.order, 10),
            target_jabatan_ids: stepFormValues.target_jabatan_ids || [],
            target_role_ids: stepFormValues.target_role_ids || []
        };

        let updatedSteps;
        if (currentStepIndex !== null) {
            // Edit existing step
            updatedSteps = currentApprovalSteps.map((step, index) =>
                index === currentStepIndex ? { ...step, ...newStep } : step
            );
        } else {
            // Add new step
            updatedSteps = [...currentApprovalSteps, newStep];
        }

        // Reorder by 'order' after adding/editing
        updatedSteps.sort((a, b) => a.order - b.order);

        // Ensure unique order or adjust if duplicates
        // This is a simple logic to re-align order if there are duplicates or jumps
        for (let i = 0; i < updatedSteps.length; i++) {
            updatedSteps[i].order = i + 1;
        }

        setCurrentApprovalSteps(updatedSteps);
        handleCloseApprovalStepsDialog();
    };

    // Handler for step name dropdown
    const handleStepNameChange = (event) => {
        const selectedValue = event.target.value;
        const selectedOption = stepNameOptions.find(option => option.value === selectedValue);
        setStepFormValues(prev => ({
            ...prev,
            step_name: selectedValue,
            order: selectedOption ? selectedOption.defaultOrder : prev.order // Auto-set order
        }));
    };

    const handleStepFormChange = (e) => {
        const { id, value, type, checked } = e.target;
        setStepFormValues(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value
        }));
    };

    const handleStepJabatanSelectChange = (event) => {
        const {
            target: { value },
        } = event;
        setStepFormValues(prev => ({
            ...prev,
            target_jabatan_ids: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    // New handler for role selection
    const handleStepRoleSelectChange = (event) => {
        const {
            target: { value },
        } = event;
        setStepFormValues(prev => ({
            ...prev,
            target_role_ids: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    // Function to reorder steps
    const handleReorderStep = useCallback((index, direction) => {
        setCurrentApprovalSteps(prevSteps => {
            const newSteps = [...prevSteps];
            const [movedItem] = newSteps.splice(index, 1);
            if (direction === 'up' && index > 0) {
                newSteps.splice(index - 1, 0, movedItem);
            } else if (direction === 'down' && index < newSteps.length) {
                newSteps.splice(index + 1, 0, movedItem);
            }

            // Re-assign orders after reordering
            return newSteps.map((step, i) => ({ ...step, order: i + 1 }));
        });
    }, []);


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    // Filter formFields to exclude 'approval_steps' so it's not rendered as a TextField
    const mainFormFields = formFields.filter(field => field.id !== 'approval_steps');
    // Find the original 'approval_steps' field to get its label
    const approvalStepsFieldConfig = formFields.find(field => field.id === 'approval_steps');


    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={() => fileInputRef.current.click()}>
                        <AddIcon />
                    </IconButton>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImport}
                        style={{ display: 'none' }}
                        accept=".xlsx, .xls"
                    />
                    <IconButton color="primary" size="small" onClick={handleExport}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            {tableHeaders.map((header, index) => (
                                <TableCell key={index}>{header.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length > 0 ? (
                            data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <IconButton
                                            aria-label="more"
                                            aria-controls={`long-menu-${row.id}`}
                                            aria-haspopup="true"
                                            onClick={(e) => handleMenuClick(e, row)}
                                            size="small"
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            id={`long-menu-${row.id}`}
                                            MenuListProps={{
                                                'aria-labelledby': 'long-button',
                                            }}
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl) && selectedRow?.id === row.id}
                                            onClose={handleCloseMenu}
                                            PaperProps={{
                                                style: {
                                                    maxHeight: 48 * 4.5,
                                                    width: '20ch',
                                                },
                                            }}
                                        >
                                            <MenuItem onClick={() => { handleCloseMenu(); handleOpenFormDialog(row); }}>
                                                <EditIcon fontSize="small" sx={{ mr: 2 }} /> Edit
                                            </MenuItem>
                                            <MenuItem onClick={() => { handleCloseMenu(); handleOpenDeleteDialog(row); }}>
                                                <DeleteIcon fontSize="small" sx={{ mr: 2 }} /> Hapus
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                    {tableHeaders.map((header, index) => (
                                        <TableCell key={index}>
                                            {/* Render for approval_steps in table */}
                                            {header.id === 'approval_steps' && typeof row[header.id] === 'object' && row[header.id] !== null ? (
                                                <Box>
                                                    {Object.values(row[header.id])
                                                        .sort((a,b) => a.order - b.order) // Sort for table display
                                                        .map((step, i) => (
                                                        <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                                            <strong>{step.order}. {step.step_name}:</strong>
                                                            {step.target_jabatan_ids && step.target_jabatan_ids.length > 0 &&
                                                                ` Jabatan: ${step.target_jabatan_ids.map(id => allJabatan.find(jab => jab.value === id)?.label || id).join(', ')}`
                                                            }
                                                            {step.target_role_ids && step.target_role_ids.length > 0 &&
                                                                ` Role: ${step.target_role_ids.map(id => allRoles.find(role => role.value === id)?.label || id).join(', ')}`
                                                            }
                                                            {!step.target_jabatan_ids?.length && !step.target_role_ids?.length && ' Tidak ada target'}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            ) : Array.isArray(row[header.id]) ? (
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {row[header.id].map((item, chipIndex) => (
                                                        <Chip key={chipIndex} label={item} size="small" />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                row[header.id]
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={tableHeaders.length + 1} align="center">
                                    Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Kontrol Paginasi */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, data.length)} of ${data.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={data.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openFormDialog} onClose={handleCloseFormDialog} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {selectedRow ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`}
                    <IconButton aria-label="close" onClick={handleCloseFormDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box component="form" sx={{ mt: 0 }}>
                        {mainFormFields.map((field) => (
                            <div key={field.id} style={{ marginBottom: 16 }}>
                                {field.type === "select" ? (
                                    <FormControl fullWidth>
                                        <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
                                        <Select
                                            labelId={`${field.id}-label`}
                                            id={field.id}
                                            value={formValues[field.id] || ""}
                                            label={field.label}
                                            onChange={handleSelectChange(field.id)}
                                        >
                                            {field.options && field.options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : field.type === "multiselect" ? (
                                    <FormControl fullWidth>
                                        <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
                                        <Select
                                            labelId={`${field.id}-label`}
                                            id={field.id}
                                            multiple
                                            value={formValues[field.id] || []}
                                            onChange={handleSelectChange(field.id)}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip key={value} label={value} onDelete={handleChipDelete(field.id, value)} />
                                                    ))}
                                                </Box>
                                            )}
                                            label={field.label}
                                        >
                                            {field.options && field.options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        autoFocus={field.autoFocus || false}
                                        margin="dense"
                                        id={field.id}
                                        label={field.label}
                                        type={field.type || "text"}
                                        fullWidth
                                        variant="outlined"
                                        value={formValues[field.id] || ""}
                                        onChange={handleFormChange}
                                        multiline={field.multiline || false}
                                        rows={field.rows || 1}
                                    />
                                )}
                            </div>
                        ))}

                        {/* Tombol untuk membuka dialog pengaturan Langkah Persetujuan */}
                        {approvalStepsFieldConfig && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>{approvalStepsFieldConfig.label}</Typography>
                                {currentApprovalSteps.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{mb:1}}>Belum ada langkah persetujuan ditambahkan.</Typography>
                                )}
                                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                    {currentApprovalSteps.map((step, index) => (
                                        <ListItem
                                            key={step.id || index}
                                            secondaryAction={
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="move up"
                                                        onClick={() => handleReorderStep(index, 'up')}
                                                        disabled={index === 0}
                                                        size="small"
                                                    >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="move down"
                                                        onClick={() => handleReorderStep(index, 'down')}
                                                        disabled={index === currentApprovalSteps.length - 1}
                                                        size="small"
                                                    >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditStep(index)} size="small">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteStep(index)} size="small">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            }
                                            sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}
                                        >
                                            <ListItemText
                                                primary={`Langkah ${step.order}: ${step.step_name}`}
                                                secondary={
                                                    <>
                                                        {step.target_jabatan_ids && step.target_jabatan_ids.length > 0 &&
                                                            <Typography component="span" variant="body2" color="text.primary">
                                                                Jabatan: {step.target_jabatan_ids.map(id => allJabatan.find(jab => jab.value === id)?.label || id).join(', ')}
                                                            </Typography>
                                                        }
                                                        {step.target_role_ids && step.target_role_ids.length > 0 &&
                                                            <Typography component="span" variant="body2" color="text.primary" sx={{ ml: step.target_jabatan_ids?.length > 0 ? 1 : 0 }}>
                                                                Role: {step.target_role_ids.map(id => allRoles.find(role => role.value === id)?.label || id).join(', ')}
                                                            </Typography>
                                                        }
                                                        {!step.target_jabatan_ids?.length && !step.target_role_ids?.length &&
                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                Tidak ada target
                                                            </Typography>
                                                        }
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddStep}
                                    sx={{ mt: 1 }}
                                >
                                    Tambah Langkah Persetujuan
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseFormDialog}>Batal</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Simpan
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog untuk Menambahkan/Mengedit Langkah Persetujuan Individu */}
            <Dialog open={openApprovalStepsDialog} onClose={handleCloseApprovalStepsDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {currentStepIndex !== null ? "Edit Langkah Persetujuan" : "Tambah Langkah Persetujuan"}
                    <IconButton aria-label="close" onClick={handleCloseApprovalStepsDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="step_name-label">Nama Langkah</InputLabel>
                        <Select
                            labelId="step_name-label"
                            id="step_name"
                            value={stepFormValues.step_name || ""}
                            label="Nama Langkah"
                            onChange={handleStepNameChange}
                            autoFocus
                        >
                            {stepNameOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        id="order"
                        label="Urutan"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={stepFormValues.order === '' ? '' : parseInt(stepFormValues.order, 10)}
                        onChange={handleStepFormChange}
                        inputProps={{ min: 1 }}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="target_jabatan_ids-label">Target Jabatan (s)</InputLabel>
                        <Select
                            labelId="target_jabatan_ids-label"
                            id="target_jabatan_ids"
                            multiple
                            value={stepFormValues.target_jabatan_ids || []}
                            onChange={handleStepJabatanSelectChange}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={allJabatan.find(jab => jab.value === value)?.label || value}
                                            onDelete={() => {
                                                setStepFormValues(prev => ({
                                                    ...prev,
                                                    target_jabatan_ids: prev.target_jabatan_ids.filter(id => id !== value)
                                                }));
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                            label="Target Jabatan (s)"
                        >
                            {allJabatan.map((jab) => (
                                <MenuItem key={jab.value} value={jab.value}>
                                    {jab.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {/* New dropdown for Target Roles */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="target_role_ids-label">Target Role (s)</InputLabel>
                        <Select
                            labelId="target_role_ids-label"
                            id="target_role_ids"
                            multiple
                            value={stepFormValues.target_role_ids || []}
                            onChange={handleStepRoleSelectChange}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={allRoles.find(role => role.value === value)?.label || value}
                                            onDelete={() => {
                                                setStepFormValues(prev => ({
                                                    ...prev,
                                                    target_role_ids: prev.target_role_ids.filter(id => id !== value)
                                                }));
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                            label="Target Role (s)"
                        >
                            {allRoles.map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                    {role.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseApprovalStepsDialog}>Batal</Button>
                    <Button onClick={handleSaveStep} variant="contained" color="primary">
                        Simpan Langkah
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Data Tabel Utama */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {title.slice(title.indexOf(" ") + 1).toLowerCase()} "{selectedRow?.[fieldName]}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default SCMConfig;
